import { buildContext } from './utils/context';

/**
 * GA4 Analytics 관련 함수
 */

// 이벤트 큐 (gtag 로드 전 이벤트 저장)
let eventQueue = [];
let isGtagReady = false;

/**
 * gtag 준비 상태 확인 및 큐 처리
 */
const checkGtagReady = () => {
  if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
    if (!isGtagReady) {
      isGtagReady = true;
      // 큐에 쌓인 이벤트 전송
      if (eventQueue.length > 0) {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[Analytics] Processing ${eventQueue.length} queued events`);
        }
        eventQueue.forEach(({ eventName, params }) => {
          try {
            window.gtag('event', eventName, params);
          } catch (error) {
            console.error('[Analytics] Error sending queued event:', error);
          }
        });
        eventQueue = [];
      }
    }
    return true;
  }
  return false;
};

/**
 * GA4 Analytics 초기화
 * @returns {boolean} 초기화 성공 여부
 */
export const initAnalytics = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  // gtag 준비 상태 확인 및 큐 처리
  if (checkGtagReady()) {
    // 개발 모드에서 디버그 옵션 활성화
    if (process.env.NODE_ENV === 'development' && window.__ANALYTICS_DEBUG__) {
      console.log('[Analytics] Debug mode enabled, gtag is ready');
    }
    return true;
  }

  // gtag가 아직 로드되지 않았으면 false 반환
  return false;
};

/**
 * GA4 이벤트 추적
 * @param {string} eventName - 이벤트 이름
 * @param {object} params - 이벤트 파라미터
 * @param {object} authStateOverride - Recoil authState를 직접 전달 (선택)
 */
export const track = (eventName, params = {}, authStateOverride = null) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    // 공통 컨텍스트 빌드 (authStateOverride 전달)
    const context = buildContext({}, authStateOverride);
    const finalParams = { ...context, ...params };

    // 필수 파라미터 검증 및 기본값 설정
    // gc_id는 항상 문자열로 강제 변환
    if (finalParams.gc_id !== undefined && finalParams.gc_id !== null) {
      finalParams.gc_id = String(finalParams.gc_id);
    } else if (finalParams.gc_id === undefined) {
      finalParams.gc_id = null;
    }
    
    // gc_name도 항상 문자열로 보장
    if (finalParams.gc_name !== undefined && finalParams.gc_name !== null) {
      finalParams.gc_name = String(finalParams.gc_name);
    } else if (finalParams.gc_name === undefined) {
      finalParams.gc_name = null;
    }
    
    if (!finalParams.page_key || finalParams.page_key === undefined) {
      finalParams.page_key = 'unknown';
    }
    if (finalParams.page_path === undefined) {
      finalParams.page_path = '';
    }
    if (finalParams.page_location === undefined) {
      finalParams.page_location = '';
    }

    // 디버그 모드
    if (process.env.NODE_ENV === 'development' && window.__ANALYTICS_DEBUG__) {
      console.log(`[Analytics] ${eventName}`, finalParams);
    }

    // gtag 준비 상태 확인
    if (checkGtagReady()) {
      // gtag가 준비되었으면 즉시 전송
      window.gtag('event', eventName, finalParams);
    } else {
      // gtag가 아직 준비되지 않았으면 큐에 저장
      eventQueue.push({ eventName, params: finalParams });
      if (process.env.NODE_ENV === 'development') {
        console.log(`[Analytics] Event queued (gtag not ready): ${eventName}`);
      }
    }
  } catch (error) {
    console.error('[Analytics] Error tracking event:', error);
  }
};
